import React, { useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Alert,
  Button,
  Field,
  IconBtn,
  Input,
  Spinner,
} from "../../design-system";
import { AuthShell } from "../../design-system/layout/AuthShell";
import { loginUser, resetLoginFlag } from "../../slices/thunks";

const selectState = createSelector(
  (state) => state,
  (state) => ({
    error: state.Login.error,
    loading: state.Login.loading,
    errorMsg: state.Login.errorMsg,
  }),
);

const Login = () => {
  document.title = "Iniciar sesión | Kronaly";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, errorMsg } = useSelector(selectState);
  const [showPassword, setShowPassword] = React.useState(false);
  const [searchParams] = useSearchParams();
  const prefilledEmail = searchParams.get("email") || "";
  const nextPath = searchParams.get("next");

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { email: prefilledEmail, password: "" },
    validationSchema: Yup.object({
      email: Yup.string().email("Introduce un correo válido").required("Introduce tu correo electrónico"),
      password: Yup.string().required("Introduce tu contraseña"),
    }),
    onSubmit: (values) => {
      dispatch(loginUser(values, navigate, nextPath));
    },
  });

  useEffect(() => {
    if (errorMsg) {
      const t = setTimeout(() => dispatch(resetLoginFlag()), 3000);
      return () => clearTimeout(t);
    }
  }, [dispatch, errorMsg]);

  return (
    <AuthShell
      footer={
        <span>
          ¿No tienes una cuenta?{" "}
          <Link to="/register" style={{ color: "var(--kr-fg-link)", fontWeight: 600 }}>
            Registrarse
          </Link>
        </span>
      }
    >
      <div className="kr-mb-4">
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "var(--kr-fg-strong)", letterSpacing: "-0.01em" }}>
          Bienvenido a Kronaly
        </h1>
        <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--kr-fg-muted)" }}>
          Inicia sesión para continuar.
        </p>
      </div>

      {errorMsg && (
        <div className="kr-mb-3">
          <Alert tone="danger">{errorMsg}</Alert>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
        }}
        className="kr-flex kr-col kr-gap-4"
      >
        <Field
          label="Correo electrónico"
          required
          error={validation.touched.email && validation.errors.email ? validation.errors.email : null}
        >
          <Input
            type="email"
            name="email"
            value={validation.values.email}
            onChange={validation.handleChange}
            placeholder="empleado@empresa.com"
            autoFocus
            inputProps={{ onBlur: validation.handleBlur, autoComplete: "email" }}
          />
        </Field>

        <div>
          <div className="kr-flex kr-justify-between kr-items-center kr-mb-2">
            <label htmlFor="password-input" style={{ fontSize: 12, fontWeight: 500, color: "var(--kr-fg-strong)" }}>
              Contraseña <span style={{ color: "var(--kr-danger-500)" }}>*</span>
            </label>
            <Link to="/forgot-password" style={{ fontSize: 12, color: "var(--kr-fg-muted)", textDecoration: "none" }}>
              ¿Olvidaste tu contraseña?
            </Link>
          </div>
          <div className="kr-relative">
            <Input
              type={showPassword ? "text" : "password"}
              name="password"
              id="password-input"
              value={validation.values.password}
              onChange={validation.handleChange}
              placeholder="Tu contraseña"
              inputProps={{ onBlur: validation.handleBlur, autoComplete: "current-password" }}
            />
            <div style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)" }}>
              <IconBtn
                icon={showPassword ? "EyeOff" : "Eye"}
                title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                size={28}
                onClick={() => setShowPassword((v) => !v)}
              />
            </div>
          </div>
          {validation.touched.password && validation.errors.password && (
            <div className="kr-mt-1 kr-fs-11" style={{ color: "var(--kr-danger-700)" }}>
              {validation.errors.password}
            </div>
          )}
        </div>

        <Button variant="primary" size="lg" type="submit" disabled={loading} fullWidth>
          {loading && <Spinner size={14} color="#fff" />}
          {loading ? "Entrando…" : "Iniciar sesión"}
        </Button>
      </form>
    </AuthShell>
  );
};

export default Login;
